import BreadCrumb from '@/components/global/BreadCrumb';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import Image from '@/components/Image';
import Seo from '@/components/seo';
import { getCategoryTreeV2 } from '@/services/category';
import { useRouter } from 'next/router';
import { handleCategoryImageURL } from '@/helpers/category';
import { useGlobalData } from '@/context/global';
import useClient from '@/hooks/global/useClient';
import {
  activeFirstLevel,
  isListedCategory,
  listedCategories,
  pickCategoriesTree,
} from '@/utils/categories/pageTree';
// import useGlobalVariables from '@/hooks/global/useGlobalVariables';

const Index = ({ tree: serverTree }) => {
  const { t } = useTranslation('common');
  const { push, locale } = useRouter();
  // QA-362: the props may arrive without the tree (a client-side navigation
  // answered an empty data payload from a cache, an upstream failure during
  // the render). The layout already holds this locale's tree in memory
  // (useCategoryTree), so the page draws from that copy then - read after
  // mount only, so the first client render matches the server html.
  const {
    appData: { categoryData },
  } = useGlobalData();
  const { isClient } = useClient();
  const tree = pickCategoriesTree(
    serverTree,
    isClient ? categoryData : null,
    locale
  );
  const availableCategories = listedCategories(tree);
  const featuredCategories = tree?.feature_categories;
  // the visitor's pick, else the first category with children - derived
  // from the current tree, so it follows a tree that arrives after mount
  const [selectedId, setSelectedId] = useState(null);
  const firstLevelActive = activeFirstLevel(availableCategories, selectedId);

  // useGlobalVariables()

  return (
    <section className='container mx-auto max-md:px-5'>
      <Seo />
      <div className='my-4'>
        <h1 className='mb-1 text-lg font-bold uppercase md:text-xl'>
          {t('Categories')}
        </h1>
        {/* <p className='mx-auto w-11/12 text-[15px] text-zinc-600 md:text-base'>
          Lorem ipsum dolor sit amet consectetur adipisicing elit. Nisi dolor
          eos dignissimos et optio tempore ducimus, omnis consequuntur modi
          eveniet.
        </p> */}
        <div className='mx-auto'>
          <ul className='gray-scroll my-2 flex items-center overflow-x-auto'>
            {featuredCategories?.map((category, i) => (
              <li
                key={`categories-page-${i}`}
                className={`cursor-pointer  border-b px-3 pb-2 text-[15px] max-sm:text-[12] `}
              >
                {category?.name}
              </li>
            ))}
          </ul>
          <div className='grid grid-cols-12 gap-3 md:gap-10'>
            <ul className='col-span-3 h-fit bg-zinc-100 px-2 max-lg:col-span-4 max-md:col-span-4'>
              {availableCategories.map((category, i) => (
                <li
                  onClick={() => {
                    category?.children_data?.length > 0
                      ? setSelectedId(category.id)
                      : push(category?.url_key);
                  }}
                  key={`categories-page-2-${i}`}
                  className={`cursor-pointer p-1 text-xs max-sm:text-[11px] min-[360px]:text-[8.5px] ${firstLevelActive?.id === category?.id ? 'font-bold' : ''}`}
                >
                  <span className='relative inline-block max-md:pb-1'>
                    {category?.name}
                    {firstLevelActive?.id === category?.id && (
                      <span className='absolute inset-x-1.5 bottom-0 h-[1.5px] rounded-full bg-[#EB1846] md:hidden' />
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className='col-span-9 grid grid-cols-3 content-start gap-2 max-lg:col-span-8 max-md:col-span-8 md:grid-cols-4 lg:grid-cols-5 min-[1500px]:grid-cols-6'>
              {firstLevelActive?.children_data?.map(
                (subCat2, i) =>
                  isListedCategory(subCat2) && (
                    <div
                      key={`categories-page-3-${i}`}
                      className='flex cursor-pointer flex-col items-center justify-center gap-0.5'
                      onClick={() => push(`/${subCat2?.url_key}`)}
                    >
                      <Image
                        loading='eager'
                        src={
                          handleCategoryImageURL(subCat2?.image) ||
                          '/imgs/placeholder.png'
                        }
                        className='h-auto max-h-32 w-full object-contain'
                        alt='Category Img!'
                      />
                      <span className='line-clamp-1 text-center text-base capitalize max-sm:text-[9px]'>
                        {subCat2?.name}
                      </span>
                    </div>
                  )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

Index.getUnderHeaderBar = (_, { t }) => (
  <BreadCrumb current={t('Categories', { ns: 'common' })} />
);

export default Index;

export async function getServerSideProps(context) {
  const { locale } = context;
  const { data: tree } = await getCategoryTreeV2(locale);
  return {
    props: {
      tree: tree?.data || {},
      ...(await serverSideTranslations(locale, ['common', 'category'])),
    },
  };
}
